import React, { useState, useEffect, useRef } from 'react';
import { myTournamentsAPI, exportAPI, familyArchersAPI, lineAssignmentsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDateRange, getStatusColor, getLevelColor, getOrgColor, getDaysUntil } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import {
  Calendar,
  MapPin,
  Target,
  Trash2,
  Edit2,
  ExternalLink,
  Clock,
  Save,
  Download,
  FileText,
  ChevronDown,
  Loader2,
  User,
  Users,
  Upload,
  Hash,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

export default function MyTournaments() {
  const { user } = useAuth();
  const isParent = user?.role === 'parent';
  const isCoach = user?.role === 'coach';
  const [tournaments, setTournaments] = useState([]);
  const [familyArchers, setFamilyArchers] = useState([]);
  const [lineAssignments, setLineAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterArcherId, setFilterArcherId] = useState('ALL');
  const [exporting, setExporting] = useState(false);
  
  // Line assignment upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadingTournament, setUploadingTournament] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Manual line assignment dialog
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualAssignment, setManualAssignment] = useState({
    tournament_id: '',
    archer_name: '',
    line_number: '',
    event_date: '',
    event_time: '',
    family_archer_id: ''
  });
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  const [editEventTime, setEditEventTime] = useState('');
  const [editEventTimezone, setEditEventTimezone] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState('INTERESTED');
  const [saving, setSaving] = useState(false);

  // Timezone display helper
  const formatTimezone = (tz) => {
    const tzNames = {
      "America/New_York": "ET",
      "America/Chicago": "CT",
      "America/Denver": "MT",
      "America/Los_Angeles": "PT",
      "America/Phoenix": "AZ",
      "Pacific/Honolulu": "HT"
    };
    return tzNames[tz] || tz;
  };

  // Export PDF handler
  const handleExportPDF = async (statusFilter = null) => {
    setExporting(true);
    try {
      const params = { include_notes: true };
      if (statusFilter) {
        params.status_filter = statusFilter;
      }
      
      const response = await exportAPI.getSchedulePDF(params);
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().split('T')[0];
      a.download = `tournament_schedule_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Schedule exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export schedule');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchMyTournaments();
    fetchLineAssignments();
    if (isParent) {
      fetchFamilyArchers();
    }
  }, [isParent]);

  useEffect(() => {
    // Re-fetch tournaments when archer filter changes (for parents)
    if (isParent) {
      fetchMyTournaments();
      fetchLineAssignments();
    }
  }, [filterArcherId, isParent]);

  const fetchFamilyArchers = async () => {
    try {
      const response = await familyArchersAPI.getAll();
      setFamilyArchers(response.data);
    } catch (error) {
      console.error('Failed to fetch family archers');
    }
  };

  const fetchMyTournaments = async () => {
    try {
      const archerId = filterArcherId !== 'ALL' ? filterArcherId : null;
      const response = await myTournamentsAPI.getAll(archerId);
      setTournaments(response.data);
    } catch (error) {
      toast.error('Failed to load your tournaments');
    } finally {
      setLoading(false);
    }
  };

  const fetchLineAssignments = async () => {
    try {
      const archerId = filterArcherId !== 'ALL' ? filterArcherId : null;
      const response = await lineAssignmentsAPI.getMyAssignments(archerId);
      setLineAssignments(response.data);
    } catch (error) {
      console.error('Failed to fetch line assignments');
    }
  };

  // Get line assignment for a tournament
  const getLineAssignment = (tournamentId, archerName) => {
    return lineAssignments.find(a => 
      a.tournament_id === tournamentId && 
      (archerName ? a.archer_name.toLowerCase().includes(archerName.toLowerCase()) : true)
    );
  };

  // Handle PDF upload
  const handleUploadPDF = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }

    setUploading(true);
    try {
      const response = await lineAssignmentsAPI.uploadPDF(uploadingTournament.id, file);
      toast.success(`Extracted ${response.data.total_extracted} line assignments`);
      if (response.data.matched_to_family > 0) {
        toast.success(`${response.data.matched_to_family} matched to your family archers`);
      }
      setUploadDialogOpen(false);
      fetchLineAssignments();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to process PDF';
      toast.error(message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle manual line assignment
  const handleAddManualAssignment = async () => {
    if (!manualAssignment.archer_name || !manualAssignment.line_number) {
      toast.error('Archer name and line number are required');
      return;
    }

    try {
      await lineAssignmentsAPI.addManual(manualAssignment);
      toast.success('Line assignment added');
      setManualDialogOpen(false);
      setManualAssignment({
        tournament_id: '',
        archer_name: '',
        line_number: '',
        event_date: '',
        event_time: '',
        family_archer_id: ''
      });
      fetchLineAssignments();
    } catch (error) {
      toast.error('Failed to add line assignment');
    }
  };

  // Open upload dialog for a tournament
  const openUploadDialog = (tournament) => {
    setUploadingTournament(tournament);
    setUploadDialogOpen(true);
  };

  // Open manual assignment dialog
  const openManualDialog = (tournament) => {
    setManualAssignment({
      ...manualAssignment,
      tournament_id: tournament.id,
      archer_name: tournament.archer_name || '',
      family_archer_id: tournament.archer_id || ''
    });
    setManualDialogOpen(true);
  };

  const handleUntrack = async (tournament) => {
    const archerName = tournament.archer_name ? ` (${tournament.archer_name})` : '';
    if (!window.confirm(`Remove this tournament${archerName} from your list?`)) return;

    try {
      await myTournamentsAPI.untrack(tournament.id, tournament.archer_id);
      setTournaments(tournaments.filter((t) => t.tracking_id !== tournament.tracking_id));
      toast.success('Tournament removed');
    } catch (error) {
      toast.error('Failed to remove tournament');
    }
  };

  const handleStatusChange = async (tournamentId, newStatus) => {
    try {
      await myTournamentsAPI.track({
        tournament_id: tournamentId,
        status: newStatus
      });
      setTournaments(
        tournaments.map((t) =>
          t.id === tournamentId ? { ...t, tracking_status: newStatus } : t
        )
      );
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openEditDialog = (tournament) => {
    setEditingTournament(tournament);
    setEditEventTime(tournament.tracking_event_time || '');
    setEditEventTimezone(tournament.tracking_event_timezone || '');
    setEditNotes(tournament.tracking_notes || '');
    setEditStatus(tournament.tracking_status || 'INTERESTED');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingTournament) return;
    
    setSaving(true);
    try {
      await myTournamentsAPI.track({
        tournament_id: editingTournament.id,
        status: editStatus,
        event_time: editEventTime || null,
        event_timezone: editEventTimezone || null,
        notes: editNotes || null
      });
      
      // Update local state
      setTournaments(
        tournaments.map((t) =>
          t.id === editingTournament.id 
            ? { 
                ...t, 
                tracking_status: editStatus,
                tracking_event_time: editEventTime || null,
                tracking_event_timezone: editEventTimezone || null,
                tracking_notes: editNotes || null
              } 
            : t
        )
      );
      
      toast.success('Tournament details updated');
      setEditDialogOpen(false);
      setEditingTournament(null);
    } catch (error) {
      toast.error('Failed to update tournament');
    } finally {
      setSaving(false);
    }
  };

  const filteredTournaments =
    filterStatus === 'ALL'
      ? tournaments
      : tournaments.filter((t) => t.tracking_status === filterStatus);

  const registeredCount = tournaments.filter((t) => t.tracking_status === 'REGISTERED').length;
  const interestedCount = tournaments.filter((t) => t.tracking_status === 'INTERESTED').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600">Loading your tournaments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-tournaments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">My Events</h1>
          <p className="text-slate-600 mt-1">
            {registeredCount} registered, {interestedCount} interested
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Archer filter for parents */}
          {isParent && familyArchers.length > 0 && (
            <Select value={filterArcherId} onValueChange={setFilterArcherId}>
              <SelectTrigger className="w-44" data-testid="archer-filter">
                <Users className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Filter by archer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Archers</SelectItem>
                {familyArchers.map((archer) => (
                  <SelectItem key={archer.id} value={archer.id}>
                    {archer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Events</SelectItem>
              <SelectItem value="REGISTERED">Registered</SelectItem>
              <SelectItem value="INTERESTED">Interested</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                disabled={exporting || tournaments.length === 0}
                data-testid="export-pdf-btn"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export PDF
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExportPDF(null)} data-testid="export-all">
                <FileText className="w-4 h-4 mr-2" />
                All Events
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExportPDF('REGISTERED')} data-testid="export-registered">
                <FileText className="w-4 h-4 mr-2 text-emerald-600" />
                Registered Only
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportPDF('INTERESTED')} data-testid="export-interested">
                <FileText className="w-4 h-4 mr-2 text-blue-600" />
                Interested Only
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Tournament List */}
      {filteredTournaments.length > 0 ? (
        <div className="space-y-4">
          {filteredTournaments.map((tournament) => {
            const daysUntil = getDaysUntil(tournament.start_date);
            const assignment = getLineAssignment(tournament.id, tournament.archer_name);

            return (
              <Card
                key={tournament.tracking_id || tournament.id}
                className="card-base"
                data-testid={`my-tournament-${tournament.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-slate-900 text-lg truncate">
                          {tournament.name}
                        </h3>
                        <Badge className={getStatusColor(tournament.tracking_status)}>
                          {tournament.tracking_status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <Badge className={getOrgColor(tournament.org)}>{tournament.org}</Badge>
                        <Badge className={getLevelColor(tournament.level)}>{tournament.level}</Badge>
                        <Badge variant="outline">{tournament.event_type}</Badge>
                        {tournament.archer_name && (
                          <Badge variant="secondary" className="bg-purple-100 text-purple-700 border-purple-200">
                            <User className="w-3 h-3 mr-1" />
                            {tournament.archer_name}
                          </Badge>
                        )}
                        {/* Line Assignment Badge */}
                        {assignment && (
                          <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">
                            <Hash className="w-3 h-3 mr-1" />
                            Line {assignment.line_number}
                            {assignment.event_time && ` @ ${assignment.event_time}`}
                          </Badge>
                        )}
                      </div>
                      <div className="space-y-1 text-sm text-slate-600">
                        <p className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {formatDateRange(tournament.start_date, tournament.end_date)}
                          {daysUntil !== null && daysUntil >= 0 && (
                            <Badge
                              variant="outline"
                              className={
                                daysUntil <= 7
                                  ? 'bg-amber-100 text-amber-700 border-amber-200'
                                  : ''
                              }
                            >
                              {daysUntil === 0 ? 'Today!' : `in ${daysUntil} days`}
                            </Badge>
                          )}
                        </p>
                        {tournament.tracking_event_time && (
                          <p className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span className="font-medium text-primary">
                              Event Time: {tournament.tracking_event_time}
                              {tournament.tracking_event_timezone && (
                                <span className="text-slate-500 ml-1">
                                  ({formatTimezone(tournament.tracking_event_timezone)})
                                </span>
                              )}
                            </span>
                          </p>
                        )}
                        <p className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          {tournament.location}
                        </p>
                      </div>
                      {tournament.tracking_notes && (
                        <p className="mt-3 text-sm text-slate-500 italic">
                          Note: {tournament.tracking_notes}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {/* Line Assignment Actions */}
                      {(isParent || isCoach) && tournament.tracking_status === 'REGISTERED' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                              data-testid={`line-assignment-btn-${tournament.id}`}
                            >
                              <Hash className="w-4 h-4 mr-1" />
                              {assignment ? 'Line ' + assignment.line_number : 'Line'}
                              <ChevronDown className="w-3 h-3 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openUploadDialog(tournament)}>
                              <Upload className="w-4 h-4 mr-2" />
                              Upload Schedule PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openManualDialog(tournament)}>
                              <Plus className="w-4 h-4 mr-2" />
                              Add Line Manually
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      
                      <Select
                        value={tournament.tracking_status}
                        onValueChange={(value) => handleStatusChange(tournament.id, value)}
                      >
                        <SelectTrigger className="w-32" data-testid={`status-select-${tournament.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="INTERESTED">Interested</SelectItem>
                          <SelectItem value="REGISTERED">Registered</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => openEditDialog(tournament)}
                        data-testid={`edit-btn-${tournament.id}`}
                        title="Edit event details"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {tournament.registration_url && (
                        <Button
                          variant="outline"
                          size="icon"
                          asChild
                          data-testid={`register-link-${tournament.id}`}
                        >
                          <a
                            href={tournament.registration_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="icon"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleUntrack(tournament)}
                        data-testid={`untrack-btn-${tournament.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="card-base">
          <CardContent className="py-12 text-center">
            <Target className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No events tracked yet</h3>
            <p className="text-slate-500 mb-4">
              Start by browsing tournaments and adding them to your list
            </p>
            <Button className="btn-primary" asChild>
              <a href="/tournaments">Browse Tournaments</a>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Edit Tournament Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Event Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editingTournament && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-900">{editingTournament.name}</h4>
                <p className="text-sm text-slate-500 mt-1">
                  {formatDateRange(editingTournament.start_date, editingTournament.end_date)}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger data-testid="edit-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INTERESTED">Interested</SelectItem>
                  <SelectItem value="REGISTERED">Registered</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Event Time</Label>
              <div className="flex gap-2">
                <Input
                  type="time"
                  value={editEventTime}
                  onChange={(e) => setEditEventTime(e.target.value)}
                  data-testid="edit-event-time"
                  className="input-base flex-1"
                />
                <Select value={editEventTimezone} onValueChange={setEditEventTimezone}>
                  <SelectTrigger className="w-[140px]" data-testid="edit-timezone-select">
                    <SelectValue placeholder="Timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/New_York">Eastern (ET)</SelectItem>
                    <SelectItem value="America/Chicago">Central (CT)</SelectItem>
                    <SelectItem value="America/Denver">Mountain (MT)</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific (PT)</SelectItem>
                    <SelectItem value="America/Phoenix">Arizona (AZ)</SelectItem>
                    <SelectItem value="Pacific/Honolulu">Hawaii (HT)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-slate-500">Select the tournament's local timezone</p>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Add any notes..."
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                data-testid="edit-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-primary" 
              onClick={handleSaveEdit} 
              disabled={saving}
              data-testid="save-edit-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Schedule PDF Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Tournament Schedule</DialogTitle>
            <DialogDescription>
              Upload a PDF containing line/lane assignments to automatically extract archer schedules.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {uploadingTournament && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{uploadingTournament.name}</p>
                <p className="text-sm text-slate-500">
                  {formatDateRange(uploadingTournament.start_date, uploadingTournament.end_date)}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Schedule PDF</Label>
              <Input
                type="file"
                accept=".pdf"
                ref={fileInputRef}
                onChange={handleUploadPDF}
                disabled={uploading}
                data-testid="schedule-pdf-input"
              />
              <p className="text-xs text-slate-500">
                We'll extract archer names, line/lane numbers, and times from the PDF.
              </p>
            </div>
            
            {uploading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
                <span className="text-slate-600">Processing PDF...</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Line Assignment Dialog */}
      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Line Assignment</DialogTitle>
            <DialogDescription>
              Manually enter the line/lane assignment details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {isParent && familyArchers.length > 0 && (
              <div className="space-y-2">
                <Label>Archer</Label>
                <Select 
                  value={manualAssignment.family_archer_id || 'NONE'}
                  onValueChange={(value) => {
                    const archer = familyArchers.find(a => a.id === value);
                    setManualAssignment({
                      ...manualAssignment,
                      family_archer_id: value === 'NONE' ? '' : value,
                      archer_name: archer?.name || manualAssignment.archer_name
                    });
                  }}
                >
                  <SelectTrigger data-testid="manual-archer-select">
                    <SelectValue placeholder="Select archer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">Other</SelectItem>
                    {familyArchers.map((archer) => (
                      <SelectItem key={archer.id} value={archer.id}>
                        {archer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Archer Name *</Label>
              <Input
                placeholder="Enter archer name"
                value={manualAssignment.archer_name}
                onChange={(e) => setManualAssignment({...manualAssignment, archer_name: e.target.value})}
                data-testid="manual-archer-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Line/Lane Number *</Label>
                <Input
                  placeholder="e.g., 5A"
                  value={manualAssignment.line_number}
                  onChange={(e) => setManualAssignment({...manualAssignment, line_number: e.target.value})}
                  data-testid="manual-line-number"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Event Time</Label>
                <Input
                  type="time"
                  value={manualAssignment.event_time}
                  onChange={(e) => setManualAssignment({...manualAssignment, event_time: e.target.value})}
                  data-testid="manual-event-time"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Event Date</Label>
              <Input
                type="date"
                value={manualAssignment.event_date}
                onChange={(e) => setManualAssignment({...manualAssignment, event_date: e.target.value})}
                data-testid="manual-event-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="btn-primary" onClick={handleAddManualAssignment} data-testid="save-manual-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
